import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Typography,
  Chip,
  InputAdornment,
  TextField,
  Button,
  Paper,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import CustomAppBar from '../content/Appbar/CustomAppBar';
import AuditLogsTable from './AuditLogsTable';
import { useApiClient, convertAxiosErrorToUserFacingMessage } from '../context/ApiClientContext';
import basicServiceHandler from '../services/basicServiceHandler';
import Toast, { useToast } from '../components/useToast';

const LOG_TYPES = [
  { value: 'admin', label: 'Admin Logs' },
  { value: 'general', label: 'General Logs' },
];

const DEFAULT_LIMIT = 50;
const PAGE_LIMIT_OPTIONS = [10, 25, 50, 100];

export default function AuditLogs() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logType, setLogType] = useState('admin');

  // Pagination state
  const [pageLimit, setPageLimit] = useState(DEFAULT_LIMIT);
  const [pagination, setPagination] = useState({
    hasNextPage: false,
    nextCursor: null,
    hasPrevPage: false,
  });
  const [cursorHistory, setCursorHistory] = useState([null]); // Stack of cursors for previous pages
  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  // Filter states
  const [availableActions, setAvailableActions] = useState([]);
  const [selectedActions, setSelectedActions] = useState([]);
  const [inputtedPerformedBy, setInputtedPerformedBy] = useState('');
  const [debouncedPerformedBy, setDebouncedPerformedBy] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const apiClient = useApiClient();
  const performedByDebounceRef = useRef(null);
  const {
    toastState,
    toastSetErrorNotification,
    toastOnClose,
  } = useToast();

  // Fetch available actions when logType changes
  useEffect(() => {
    const fetchActions = () => (apiClient.getAuditLogActions(logType));

    basicServiceHandler(
      fetchActions,
      (result) => {
        setAvailableActions(result.data?.actions || []);
      },
      () => {
        setAvailableActions([]);
      },
    );
    // Reset selected actions when log type changes
    setSelectedActions([]);
  }, [logType]);

  // Fetch audit logs with explicit parameters
  const fetchAuditLogs = useCallback((params) => {
    const {
      type = logType,
      actions = selectedActions,
      performedBy = debouncedPerformedBy,
      start = startDate,
      end = endDate,
      limit = pageLimit,
      cursor = null,
      pageIndex = currentPageIndex,
    } = params || {};

    setLoading(true);

    const queryParams = { limit };
    if (actions.length > 0) {
      queryParams.action = actions.join(',');
    }
    if (performedBy) {
      queryParams.performedBy = performedBy;
    }
    if (start) {
      queryParams.startDate = start.toISOString();
    }
    if (end) {
      queryParams.endDate = end.toISOString();
    }
    if (cursor) {
      queryParams.cursor = cursor;
    }

    const apiCall = () => (apiClient.getAuditLogs(type, queryParams));

    basicServiceHandler(
      apiCall,
      (result) => {
        setAuditLogs(result.data?.data || []);
        setPagination({
          hasNextPage: result.data?.pagination?.hasNextPage || false,
          nextCursor: result.data?.pagination?.nextCursor || null,
          hasPrevPage: pageIndex > 0,
        });
        setLoading(false);
      },
      (error) => {
        setAuditLogs([]);
        toastSetErrorNotification(convertAxiosErrorToUserFacingMessage(error));
        setLoading(false);
      },
    );
  }, [
    logType,
    selectedActions,
    debouncedPerformedBy,
    startDate,
    endDate,
    pageLimit,
    currentPageIndex,
    apiClient,
    toastSetErrorNotification,
  ]);

  // Helper to reset pagination and fetch fresh data
  const resetAndFetch = (overrides = {}) => {
    setCursorHistory([null]);
    setCurrentPageIndex(0);
    fetchAuditLogs({
      ...overrides, cursor: null, pageIndex: 0,
    });
  };

  // Debounced handler for performedBy input
  const onChangeInputtedPerformedBy = useCallback((newValue) => {
    setInputtedPerformedBy(newValue);

    if (performedByDebounceRef.current) {
      clearTimeout(performedByDebounceRef.current);
    }

    performedByDebounceRef.current = setTimeout(() => {
      setDebouncedPerformedBy(newValue);
      setCursorHistory([null]);
      setCurrentPageIndex(0);
      fetchAuditLogs({ performedBy: newValue, cursor: null, pageIndex: 0 });
    }, 500);
  }, [fetchAuditLogs]);

  // Initial fetch on mount
  useEffect(() => {
    fetchAuditLogs({ cursor: null });
  }, []);

  const handleNextPage = () => {
    if (pagination.hasNextPage && pagination.nextCursor) {
      const newHistory = [...cursorHistory, pagination.nextCursor];
      const newIndex = currentPageIndex + 1;
      setCursorHistory(newHistory);
      setCurrentPageIndex(newIndex);
      fetchAuditLogs({ cursor: pagination.nextCursor, pageIndex: newIndex });
    }
  };

  const handlePrevPage = () => {
    if (currentPageIndex > 0) {
      const newIndex = currentPageIndex - 1;
      const prevCursor = cursorHistory[newIndex];
      setCurrentPageIndex(newIndex);
      fetchAuditLogs({ cursor: prevCursor, pageIndex: newIndex });
    }
  };

  const handleLogTypeChange = (event) => {
    const newLogType = event.target.value;
    setLogType(newLogType);
    resetAndFetch({ type: newLogType, actions: [] });
  };

  const handleActionsChange = (event) => {
    const newActions = event.target.value;
    setSelectedActions(newActions);
    resetAndFetch({ actions: newActions });
  };

  const handlePageLimitChange = (newLimit) => {
    setPageLimit(newLimit);
    resetAndFetch({ limit: newLimit });
  };

  const handleStartDateChange = (newDate) => {
    setStartDate(newDate);
    resetAndFetch({ start: newDate });
  };

  const handleEndDateChange = (newDate) => {
    setEndDate(newDate);
    resetAndFetch({ end: newDate });
  };

  const handleClearFilters = () => {
    // Clear any pending debounce
    if (performedByDebounceRef.current) {
      clearTimeout(performedByDebounceRef.current);
    }
    setSelectedActions([]);
    setInputtedPerformedBy('');
    setDebouncedPerformedBy('');
    setStartDate(null);
    setEndDate(null);
    resetAndFetch({
      actions: [], performedBy: '', start: null, end: null,
    });
  };

  const hasActiveFilters = selectedActions.length > 0
    || inputtedPerformedBy
    || startDate
    || endDate;

  return (
    <>
      <CustomAppBar name="Audit Logs" />
      <Box sx={{ padding: '30px' }}>
        {/* Filters Container */}
        <Paper
          sx={{
            p: 2,
            mb: 0,
            borderRadius: '16px 16px 0 0',
            border: '1px solid #e0e0e0',
            borderBottom: 'none',
          }}
        >
          {/* Log Type Selector */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>Log Type:</Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <Select
                value={logType}
                onChange={handleLogTypeChange}
                input={<OutlinedInput />}
                sx={{ maxHeight: '44px' }}
              >
                {LOG_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Filters Row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {/* Actions Multi-Select */}
            <FormControl sx={{ minWidth: 200, flex: '1 1 200px', maxWidth: 400 }}>
              <Select
                multiple
                value={selectedActions}
                onChange={handleActionsChange}
                input={<OutlinedInput />}
                displayEmpty
                sx={{ maxHeight: '44px' }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span style={{ color: '#666' }}>Filter by Action</span>;
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  );
                }}
              >
                {availableActions.map((action) => (
                  <MenuItem key={action} value={action}>
                    {action}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Performed By Search */}
            <TextField
              placeholder="Filter by Performed By"
              value={inputtedPerformedBy}
              onChange={(e) => onChangeInputtedPerformedBy(e.target.value)}
              sx={{ minWidth: 200, flex: '1 1 200px', maxWidth: 300 }}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <MagnifyingGlassIcon style={{ width: 20, height: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Date Pickers */}
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 150, flex: '0 1 170px' },
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 150, flex: '0 1 170px' },
                  },
                }}
              />
            </LocalizationProvider>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </Paper>

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              border: '1px solid #e0e0e0',
              borderRadius: '0 0 16px 16px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <AuditLogsTable
            data={auditLogs}
            logType={logType}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={currentPageIndex > 0}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            currentPage={currentPageIndex + 1}
            pageLimit={pageLimit}
            pageLimitOptions={PAGE_LIMIT_OPTIONS}
            onPageLimitChange={handlePageLimitChange}
          />
        )}
      </Box>
      <Toast
        open={toastState.open}
        severity={toastState.severity}
        text={toastState.text}
        onClose={toastOnClose}
      />
    </>
  );
}
